import React, { useState, useEffect, useRef } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Plus, X, Trash2, RefreshCw } from 'lucide-react';
import { formatCurrency, getCurrencySymbol } from '../../lib/utils';

// ── WebGL domain-warp FBM shaders ──────────────────────────────
const VERT_SRC = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0., 1.); }
`;

const FRAG_SRC = `
precision mediump float;
uniform float u_time;
uniform vec2 u_res;
uniform int u_light;

float hash(vec2 p) {
 return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453);
}
float noise(vec2 p) {
 vec2 i = floor(p), f = fract(p);
 vec2 u = f*f*(3.-2.*f);
 return mix(mix(hash(i),hash(i+vec2(1,0)),u.x),
 mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x),u.y);
}
float fbm(vec2 p) {
 float v=0.,a=.5;
 mat2 rot=mat2(cos(.5),sin(.5),-sin(.5),cos(.5));
 for(int i=0;i<6;i++){v+=a*noise(p);p=rot*p*2.1;a*=.48;}
 return v;
}

void main() {
 vec2 uv = gl_FragCoord.xy / u_res;
 float t = u_time * 0.008;

 vec2 p = uv * 2.8;
 vec2 q = vec2(fbm(p+t), fbm(p+vec2(5.2,1.3)+t*.9));
 vec2 r = vec2(fbm(p+4.*q+vec2(1.7,9.2)+t*.6),
 fbm(p+4.*q+vec2(8.3,2.8)+t*.5));
 float f = fbm(p + 4.*r);

 vec3 col;
 if (u_light == 0) {
 vec3 dark = vec3(0.03, 0.035, 0.045);
 vec3 mid = vec3(0.09, 0.105, 0.13);
 vec3 light = vec3(0.18, 0.20, 0.24);
 col = mix(dark, mid, clamp(f*2., 0., 1.));
 col = mix(col, light, clamp(f*2.-1., 0., 1.));
 col += vec3(0.02, 0.01, 0.0) * f * f;
 } else {
 vec3 base = vec3(0.788, 0.804, 0.820);
 vec3 mid = vec3(0.680, 0.695, 0.715);
 vec3 vein = vec3(0.560, 0.575, 0.600);
 col = mix(base, mid, clamp(f*2., 0., 1.));
 col = mix(col, vein, clamp(f*2.-1., 0., 1.));
 col -= vec3(0.01, 0.005, 0.0) * (1.-f) * (1.-f);
 }

 gl_FragColor = vec4(col, 1.);
}
`;

function useWarpShader(canvasRef, isLight) {
 const tRef = useRef(0);

 useEffect(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;

 const rect = canvas.parentElement.getBoundingClientRect();
 canvas.width = rect.width * window.devicePixelRatio;
 canvas.height = rect.height * window.devicePixelRatio;

 const gl = canvas.getContext('webgl');
 if (!gl) {
 canvas.style.background = isLight
 ? 'linear-gradient(135deg, hsl(210 7% 74%) 0%, hsl(210 7% 80%) 50%, hsl(210 7% 70%) 100%)'
 : 'linear-gradient(135deg, hsl(220 18% 10%) 0%, hsl(220 18% 14%) 50%, hsl(220 18% 9%) 100%)';
 canvas.style.animation = 'shaderFallback 6s ease-in-out infinite alternate';
 return;
 }

 const compile = (type, src) => {
 const s = gl.createShader(type);
 gl.shaderSource(s, src); gl.compileShader(s); return s;
 };
 const prog = gl.createProgram();
 gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT_SRC));
 gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG_SRC));
 gl.linkProgram(prog); gl.useProgram(prog);

 const buf = gl.createBuffer();
 gl.bindBuffer(gl.ARRAY_BUFFER, buf);
 gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,1,1]), gl.STATIC_DRAW);
 const loc = gl.getAttribLocation(prog, 'a_pos');
 gl.enableVertexAttribArray(loc);
 gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

 const uTime = gl.getUniformLocation(prog, 'u_time');
 const uRes = gl.getUniformLocation(prog, 'u_res');
 const uLight = gl.getUniformLocation(prog, 'u_light');
 gl.uniform2f(uRes, canvas.width, canvas.height);
 gl.uniform1i(uLight, isLight ? 1 : 0);

 let rafId;
 const draw = () => {
 gl.uniform1f(uTime, tRef.current += 0.30);
 gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
 rafId = requestAnimationFrame(draw);
 };
 draw();

 return () => { cancelAnimationFrame(rafId); };
 }, [canvasRef, isLight]);
}

// ── Animated number hook ────────────────────────────────────────
const useAnimatedValue = (target, duration = 600) => {
 const [display, setDisplay] = useState(target);
 const prev = useRef(target);
 const rafId = useRef(null);

 useEffect(() => {
 const start = prev.current;
 const end = target;
 if (start === end) return;
 if (rafId.current) cancelAnimationFrame(rafId.current);
 const startTime = performance.now();
 const tick = (now) => {
 const t = Math.min((now - startTime) / duration, 1);
 const eased = 1 - Math.pow(1 - t, 3);
 setDisplay(start + (end - start) * eased);
 if (t < 1) {
 rafId.current = requestAnimationFrame(tick);
 } else {
 prev.current = end;
 }
 };
 rafId.current = requestAnimationFrame(tick);
 return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
 }, [target, duration]);

 return display;
};

// ── Component ───────────────────────────────────────────────────
const SimpleMobileMode = ({
 categories,
 categoryGroups,
 lastUsedCategory,
 monthlyIncome,
 totalSpent,
 transactions,
 allocations,
 onAddExpense,
 onDeleteTransaction,
 onShowToast,
 onUpdateIncome,
 pendingRecurring = [],
 onShowRecurring,
}) => {
 const [amount, setAmount] = useState('');
 const [displayAmount, setDisplayAmount] = useState('');
 const [selectedCategory, setSelectedCategory] = useState(lastUsedCategory);
 const [showCategoryPicker, setShowCategoryPicker] = useState(false);
 const [editingIncome, setEditingIncome] = useState(false);
 const [showSpentModal, setShowSpentModal] = useState(false);
 const [incomeInput, setIncomeInput] = useState(monthlyIncome.toString());
 const [currencySymbol, setCurrencySymbol] = useState(getCurrencySymbol());
 const [isLight, setIsLight] = useState(!document.documentElement.classList.contains('dark'));
 const canvasRef = useRef(null);
 const pendingSubmit = useRef(false);

 // Detect theme changes
 useEffect(() => {
 const observer = new MutationObserver(() => {
 setIsLight(!document.documentElement.classList.contains('dark'));
 });
 observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
 return () => observer.disconnect();
 }, []);

 useEffect(() => {
 const handleCurrencyChange = () => setCurrencySymbol(getCurrencySymbol());
 window.addEventListener('currencyChange', handleCurrencyChange);
 return () => window.removeEventListener('currencyChange', handleCurrencyChange);
 }, []);

 useEffect(() => {
 if (lastUsedCategory && !selectedCategory) setSelectedCategory(lastUsedCategory);
 }, [lastUsedCategory, selectedCategory]);

 useWarpShader(canvasRef, isLight);

 const handleAmountChange = (e) => {
 const raw = e.target.value.replace(/[^\d.]/g, '');
 setAmount(raw);
 const num = parseFloat(raw);
 setDisplayAmount(raw === '' ? '' : isNaN(num) ? raw : new Intl.NumberFormat('en').format(num));
 };

 const submitExpense = async (category) => {
 try {
 await onAddExpense({
 category_id: category.id,
 amount: parseFloat(amount),
 description: '',
 date: new Date().toISOString().split('T')[0],
 });
 localStorage.setItem('lastUsedCategoryId', category.id);
 setAmount(''); setDisplayAmount('');
 onShowToast('Added!', 'success');
 } catch {
 onShowToast('Add failed', 'error');
 }
 };

 const handleCategorySelect = async (category) => {
 setSelectedCategory(category);
 setShowCategoryPicker(false);
 localStorage.setItem('lastUsedCategoryId', category.id);
 if (pendingSubmit.current && amount && parseFloat(amount) > 0) {
 pendingSubmit.current = false;
 await submitExpense(category);
 }
 };

 const handleAddExpense = async () => {
 if (!amount || parseFloat(amount) <= 0) { onShowToast('Enter a valid amount', 'error'); return; }
 if (!selectedCategory) { pendingSubmit.current = true; setShowCategoryPicker(true); return; }
 await submitExpense(selectedCategory);
 };

 const available = monthlyIncome - totalSpent;
 const totalBudgeted = Array.from(allocations?.values() || []).reduce((s, v) => s + v, 0);
 const animatedAvailable = useAnimatedValue(available);
 const animatedSpent = useAnimatedValue(totalSpent);

 // Icon colour flips with theme
 const plusStroke = isLight ? '#1a1d24' : '#c9cdd1';

 return (
 <div className="fixed inset-0 flex flex-col overflow-hidden bg-background pt-16 safe-area-simple-mode">

 {/* ── Income Edit Modal ── */}
 {editingIncome && (
 <>
 <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setEditingIncome(false)} />
 <div className="fixed inset-x-0 bottom-0 bg-background z-50">
 <div className="p-6 space-y-6">
 <div className="flex items-center justify-between">
 <h3 className="text-xl font-medium text-foreground">Edit Monthly Income</h3>
 <button onClick={() => setEditingIncome(false)} className="p-2 hover:bg-accent transition-colors">
 <X className="h-6 w-6 text-muted-foreground" />
 </button>
 </div>
 <div className="space-y-4">
 <div>
 <label className="text-base font-medium text-foreground mb-3 block">Monthly Income</label>
 <div className="relative">
 <span className="absolute left-5 top-1/2 -translate-y-1/2 text-3xl text-muted-foreground font-mono">{currencySymbol}</span>
 <Input type="text" inputMode="decimal" value={incomeInput}
 onChange={(e) => setIncomeInput(e.target.value)}
 placeholder="0.00" className="h-16 pl-14 !text-2xl font-medium font-mono" autoFocus />
 </div>
 </div>
 <Button onClick={() => { const n = parseFloat(incomeInput) || 0; onUpdateIncome(n); setEditingIncome(false); onShowToast('Saved!', 'success'); }}
 className="w-full h-14 text-lg font-medium">Save</Button>
 </div>
 </div>
 </div>
 </>
 )}

 {/* ── Category Picker Modal ── */}
 {showCategoryPicker && (
 <>
 <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCategoryPicker(false)} />
 <div className="fixed inset-x-0 bottom-0 bg-background z-50 max-h-[70vh] overflow-y-auto">
 <div className="p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-xl font-medium text-foreground">Select Category</h3>
 <button onClick={() => setShowCategoryPicker(false)} className="p-2 hover:bg-accent transition-colors">
 <X className="h-6 w-6 text-muted-foreground" />
 </button>
 </div>
 <div className="space-y-4">
 {categoryGroups.map(group => {
 const groupCategories = categories.filter(c => c.group_id === group.id);
 return (
 <div key={group.id}>
 <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">{group.name}</h4>
 <div className="space-y-1">
 {groupCategories.map(cat => {
 const catSpent = (transactions || []).filter(t => t.category_id === cat.id && t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
 const catBudget = allocations ? (allocations.get(cat.id) || 0) : 0;
 const catRemaining = catBudget - catSpent;
 return (
 <button key={cat.id} onClick={() => handleCategorySelect(cat)}
 className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent transition-colors text-foreground">
 <span>{cat.name}</span>
 <span className={catRemaining < 0 ? 'text-destructive text-sm font-medium' : 'text-success text-sm font-medium'}>
 {catRemaining >= 0 ? `${formatCurrency(catRemaining)} left` : `${formatCurrency(Math.abs(catRemaining))} over`}
 </span>
 </button>
 );
 })}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </div>
 </>
 )}

 {/* ── Spent Transactions Modal ── */}
 {showSpentModal && (
 <>
 <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowSpentModal(false)} />
 <div className="fixed inset-x-0 bottom-0 bg-background z-50 max-h-[70vh] overflow-y-auto">
 <div className="p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-xl font-medium text-foreground">Recent Spending</h3>
 <button onClick={() => setShowSpentModal(false)} className="p-2 hover:bg-accent transition-colors">
 <X className="h-6 w-6 text-muted-foreground" />
 </button>
 </div>
 <div className="space-y-2">
 {(() => {
 const expenses = (transactions || []).filter(t => t.amount < 0);
 return expenses.length > 0 ? (
 expenses
 .sort((a, b) => new Date(b.date) - new Date(a.date))
 .slice(0, 20)
 .map(txn => {
 const category = categories.find(c => c.id === txn.category_id);
 return (
 <div key={txn.id} className="flex items-center justify-between p-3 bg-background group">
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-foreground truncate">{txn.description || category?.name || 'Uncategorized'}</p>
 <p className="text-xs text-muted-foreground mt-1">
 {new Date(txn.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
 {category && ` • ${category.name}`}
 </p>
 </div>
 <div className="flex items-center gap-2 ml-3">
 <p className="text-base font-semibold text-foreground">{formatCurrency(Math.abs(txn.amount))}</p>
 <button onClick={() => onDeleteTransaction(txn.id)}
 className="p-2 hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100">
 <Trash2 className="h-4 w-4 text-destructive" />
 </button>
 </div>
 </div>
 );
 })
 ) : (
 <div className="text-center py-8">
 <p className="text-muted-foreground">No transactions yet</p>
 </div>
 );
 })()}
 </div>
 </div>
 </div>
 </>
 )}

 {/* ── Top Stats — fixed height, shrink-0 ── */}
 <div className="flex-shrink-0 flex flex-col items-center justify-center px-6 py-8 gap-5 border-b border-border">
 {/* Recurring banner */}
 {pendingRecurring.length > 0 && (
 <button
 onClick={onShowRecurring}
 className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:opacity-80 transition-opacity w-full justify-center"
 >
 <RefreshCw className="h-4 w-4 flex-shrink-0" />
 {pendingRecurring.length} recurring to review
 </button>
 )}
 {/* Available */}
 <div className="text-center">
 <p className="text-xs font-medium uppercase tracking-widest text-foreground/25 mb-3">Available</p>
 <p className="text-5xl font-medium font-mono tabular-nums text-foreground">
 {formatCurrency(animatedAvailable)}
 </p>
 </div>

 {/* Three mini stats — ledger strip */}
 <div className="grid grid-cols-3 w-full border border-border divide-x divide-border">
 <button
 onClick={() => { setIncomeInput(monthlyIncome.toString()); setEditingIncome(true); }}
 className="text-center px-3 py-2 active:bg-accent transition-colors"
 >
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Income</p>
 <p className="text-sm font-mono tabular-nums text-foreground">{formatCurrency(monthlyIncome)}</p>
 </button>
 <div className="text-center px-3 py-2">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Budgeted</p>
 <p className="text-sm font-mono tabular-nums text-foreground">{formatCurrency(totalBudgeted)}</p>
 </div>
 <button onClick={() => setShowSpentModal(true)} className="text-center px-3 py-2 active:bg-accent transition-colors">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Spent</p>
 <p className="text-sm font-mono tabular-nums text-foreground">{formatCurrency(animatedSpent)}</p>
 </button>
 </div>
 </div>

 {/* ── Bottom: Input + WebGL button — takes remaining vh ── */}
 <div className="flex-1 flex flex-col min-h-0">
 {/* Amount input */}
 <div className="border-b border-border px-6 py-4">
 <div className="flex items-center justify-between mb-2">
 <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Amount</label>
 <button onClick={() => setShowCategoryPicker(true)}
 className="text-xs text-foreground hover:opacity-60 transition-opacity font-medium">
 {selectedCategory ? `${selectedCategory.name} →` : 'Select Category →'}
 </button>
 </div>
 <div className="relative flex items-center">
 <span className="text-muted-foreground font-mono mr-2" style={{ fontSize: '36px' }}>{currencySymbol}</span>
 <Input type="text" inputMode="decimal" value={displayAmount} onChange={handleAmountChange}
 placeholder="0"
 className="flex-1 h-auto py-0 px-0 font-medium font-mono border-0 bg-transparent focus:ring-0 placeholder:text-muted-foreground/30"
 style={{ fontSize: '36px' }} />
 </div>
 </div>

 {/* WebGL domain warp button — fills rest */}
 <button
 onClick={handleAddExpense}
 className="flex-1 relative overflow-hidden transition-opacity active:opacity-90 flex items-center justify-center min-h-0"
 >
 <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ imageRendering: 'crisp-edges' }} />
 <Plus
 className="relative z-10"
 style={{ width: '160px', height: '160px' }}
 stroke={plusStroke}
 strokeWidth={1}
 strokeLinecap="square"
 strokeLinejoin="miter"
 />
 </button>
 </div>
 </div>
 );
};

export default SimpleMobileMode;
